import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { INTERESTS } from "@/lib/data";
import { useLang } from "@/i18n";
import { trackEvent } from "@/lib/analytics";
import { ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", company: "", country: "", interest: "", message: "" };

const Field = ({ label, id, ...props }) => (
  <label htmlFor={id} className="block">
    <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#5f6b57]">{label}</span>
    <input
      id={id}
      {...props}
      className="w-full border-0 border-b border-[#1d3a1f]/20 bg-transparent py-3 mt-2 text-[#1d3a1f] placeholder:text-[#a89a86] focus:outline-none focus:border-[#1d3a1f] transition-colors duration-200"
    />
  </label>
);

/** B2B Request-a-Quote form. Posts to /api/leads (persists + emails owner). */
const ContactForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t, data } = useLang();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.country || !form.interest || !form.message) {
      toast.error(t("form.incomplete"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackEvent("generate_lead", { interest: form.interest });
      setDone(true);
      setForm(empty);
      toast.success(t("form.successToast"));
    } catch (err) {
      console.error("Lead submission failed:", err);
      if (err.response?.status === 429) toast.error(t("form.tooMany"));
      else toast.error(t("form.error"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="h-full min-h-[300px] flex flex-col items-center justify-center text-center border border-[#d3a53a]/30 p-12">
        <div className="w-16 h-16 rounded-full bg-[#d3a53a] flex items-center justify-center"><Check className="text-[#fdfbf5]" size={32} /></div>
        <h3 className="mt-6 font-display font-bold text-2xl">{t("form.successTitle")}</h3>
        <p className="mt-3 text-[#5f6b57] max-w-sm">{t("form.successBody")}</p>
        <button onClick={() => setDone(false)} className="mt-8 text-sm font-bold text-[#d3a53a] hover:text-[#1d3a1f] transition-colors">{t("form.successAgain")}</button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
      <Field label={t("form.name")} id="name" data-testid="input-name" placeholder={t("form.namePh")} value={form.name} onChange={set("name")} />
      <Field label={t("form.email")} id="email" type="email" data-testid="input-email" placeholder={t("form.emailPh")} value={form.email} onChange={set("email")} />
      <Field label={t("form.company")} id="company" data-testid="input-company" placeholder={t("form.companyPh")} value={form.company} onChange={set("company")} />
      <Field label={t("form.country")} id="country" data-testid="input-country" placeholder={t("form.countryPh")} value={form.country} onChange={set("country")} />
      <label htmlFor="interest" className="block sm:col-span-2">
        <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#5f6b57]">{t("form.interest")}</span>
        <select
          id="interest"
          data-testid="input-interest"
          value={form.interest}
          onChange={set("interest")}
          className="w-full border-0 border-b border-[#1d3a1f]/20 bg-transparent py-3 mt-2 text-[#1d3a1f] focus:outline-none focus:border-[#1d3a1f] transition-colors duration-200 [&>option]:bg-[#ffffff]"
        >
          <option value="">{t("form.interestPrompt")}</option>
          {/* Values stay English (canonical in the inbox); labels are localised. */}
          {INTERESTS.map((o, i) => <option key={o} value={o}>{data.interests?.[i] || o}</option>)}
        </select>
      </label>
      <label htmlFor="message" className="block sm:col-span-2">
        <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#5f6b57]">{t("form.message")}</span>
        <textarea
          id="message"
          data-testid="input-message"
          rows={4}
          placeholder={t("form.messagePh")}
          value={form.message}
          onChange={set("message")}
          className="w-full border-0 border-b border-[#1d3a1f]/20 bg-transparent py-3 mt-2 text-[#1d3a1f] placeholder:text-[#a89a86] focus:outline-none focus:border-[#1d3a1f] transition-colors duration-200 resize-none"
        />
      </label>
      <button
        data-testid="contact-submit"
        type="submit"
        disabled={loading}
        className="sm:col-span-2 group inline-flex items-center justify-center gap-2 bg-[#d3a53a] text-[#173a1e] font-bold px-8 py-4 rounded-full hover:bg-[#b98a26] transition-colors duration-200 disabled:opacity-60"
      >
        {loading ? t("form.sending") : t("form.submit")}
        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
      </button>
    </form>
  );
};

export default ContactForm;
